const CHANNEL_NAME = 'bips-toast-sync';
const STORAGE_KEY = 'bips-toast-sync-event';
const EMITTER_ID = `${Date.now().toString(36)}-${Math.random()
  .toString(36)
  .slice(2)}`;

type ToastSyncEvent = {
  type: 'dismiss';
  toastId: string;
  originId: string;
  timestamp: number;
};

type DismissListener = (toastId: string) => void;

const listeners = new Set<DismissListener>();

const hasWindow = typeof window !== 'undefined';
const hasBroadcastChannel =
  hasWindow && typeof (window as any).BroadcastChannel !== 'undefined';

let broadcastChannel: BroadcastChannel | null = null;
let storageListener: ((event: StorageEvent) => void) | null = null;

const notifyListeners = (event: ToastSyncEvent) => {
  if (event.originId === EMITTER_ID) return;
  if (event.type !== 'dismiss') return;

  listeners.forEach((listener) => listener(event.toastId));
};

const ensureChannel = () => {
  if (!hasWindow) return;

  // Use broadcast channel if avaliable in the browser:
  if (hasBroadcastChannel) {
    if (!broadcastChannel) {
      broadcastChannel = new window.BroadcastChannel(CHANNEL_NAME);
      broadcastChannel.onmessage = (event: MessageEvent<ToastSyncEvent>) => {
        if (!event?.data) return;
        notifyListeners(event.data);
      };
    }
    // Fallback to listening to storage events for older browsers:
  } else if (!storageListener) {
    storageListener = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      if (!event.newValue) return;

      try {
        const parsed = JSON.parse(event.newValue) as ToastSyncEvent;
        notifyListeners(parsed);
      } catch (error) {
        console.warn('Failed to parse toast sync event', error);
      }
    };

    window.addEventListener('storage', storageListener);
  }
};

export const subscribeToToastDismiss = (listener: DismissListener) => {
  if (!hasWindow) return () => {};

  ensureChannel();
  listeners.add(listener);

  return () => {
    listeners.delete(listener);

    if (listeners.size === 0) {
      if (broadcastChannel) {
        broadcastChannel.close();
        broadcastChannel = null;
      }

      if (storageListener) {
        window.removeEventListener('storage', storageListener);
        storageListener = null;
      }
    }
  };
};

export const broadcastToastDismiss = (toastId: string) => {
  if (!hasWindow) return;

  const event: ToastSyncEvent = {
    type: 'dismiss',
    toastId,
    originId: EMITTER_ID,
    timestamp: Date.now()
  };

  if (broadcastChannel) {
    broadcastChannel.postMessage(event);
    return;
  }

  try {
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(event));
    // Cleanup the storage key to avoid growth
    window.localStorage?.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to broadcast toast dismiss via localStorage', error);
  }
};
